/**
 * projeTaahhutnamePdf.js — the project's three taahhütnames (İSG / Elektrik / Statik) as downloadable PDFs, Turkish or English (backlog #666,
 * the frontend half of #665).
 *
 * One generator, three documents: they differ by the title, the wording and the signature blocks only. ONE landscape A4 sheet in boxes, like the
 * PHP originals (Deniz, 2026-11-02: "squeeze them all into a single page"):
 *   top left     the fair logo | the header (Fuarın Adı, dates, Katılımcı Firma, Dekoratör Firma, Salon - Stand No) | the SAKA GRUP logo,
 *                then the title (text.baslik, else the document's own name);
 *   left middle  the source image the inspector chose (a JPG / PNG, or the first page of a PDF), fitted in the box with its proportions kept;
 *   right column the wording (GET /projects/{id}/taahhutnameler -> text.icerik, HTML, read by taahhutnameHtml.js): paragraphs, bold / italic,
 *                numbered and bulleted lists, in the largest type that fits the column (8.5 pt down to 5 pt);
 *   bottom band  the signature blocks of that document, ALL BLANK (nothing is prefilled: not the approval given in the app, not the electrician
 *                recorded by #505): Statik = Stand Teknik Uygulama Sorumlusu + Dekoratör Firma Yetkilisi; Elektrik = Dekoratör Firma Yetkilisi +
 *                Stand Elektrik Sorumlusu; İSG = Dekoratör Firma Yetkilisi. An <hr /> splits the wording into sections: the LAST one (Statik: the
 *                Stand Teknik Uygulama Sorumlusu's own declaration) sits in the band, left of the blocks, as in the PHP sheet.
 * A wording too long even for 5 pt is the one case that needs more room: the column then says so and the full wording runs on the sheets after
 * the first (10 pt, full width, running head, "Sayfa n / N"). No wording-version footer (Deniz, 2026-11-02).
 *
 * No network here: `veri` and `varliklar` come from projeTaahhutnameService.js (and the tests). pdf-lib and fontkit are imported lazily like
 * everywhere else. Labels follow `dil` (the language the PDF is made in), never the UI language; the wording is whatever the backend sent for
 * that language (Turkish when no English version is published yet, text.languageId says which).
 *
 * veri = { key: "isg" | "elektrik" | "statik",
 *          header: { fuarAdi, fuarBaslangic, fuarBitis, salonAdi, standAdi, katilimciFirmaAdi, dekoratorFirmaAdi },
 *          text: { baslik, icerik (HTML), surumNo, fuarId } | null,
 *          kaynak: { bytes: Uint8Array, tur: "png" | "jpg" | "pdf" } | null,
 *          logo: Uint8Array (PNG / JPEG) | null }
 * varliklar = { font, fontKalin, fontItalik: Uint8Array (Liberation Sans, TrueType with Latin Extended-A), sakaLogo: Uint8Array (PNG) }
 */
import i18n from "../i18n";
import { htmlBloklari, bolumlereAyir } from "./taahhutnameHtml";
import { resimTuru, dosyaParcasi } from "./isgTaahhutnamePdf";

export const PROJE_TAAHHUTNAME_ANAHTARLARI = ["isg", "elektrik", "statik"];
export const PROJE_TAAHHUTNAME_DILLERI = ["tr", "en"];

// ── the sheet, points from the TOP-left ──
const SAYFA = { en: 841.89, boy: 595.28 };
const KENAR = 28;
const X0 = KENAR;
const X1 = SAYFA.en - KENAR;
const Y0 = KENAR;
const Y1 = SAYFA.boy - KENAR;
const SAG_KOLON_EN = 252;          // the wording's column
const XB = X1 - SAG_KOLON_EN;      // where the left part ends and the column begins
const LOGO_HUCRE_EN = 92;
const LOGO_IC = 5;
const ILK_SATIR_EN_AZ = 58;        // top row: at least this high
const BASLIK_YUK = 23;
const BASLIK_PUNTO = 12.5;
const BASLIK_TABLO_PUNTO = 8;
const BAND_YUK = 94;
const BAND_BASLIK = 15;
const BAND_ALAN_ARASI = 17;
const BAND_BLOK_EN_FAZLA = 392;
const BAND_BEYAN_ORANI = 0.34;     // the share of the band the last section's text takes when there is one
const SIGDIR_BUYUK = 8.5;          // the wording's type: the largest that fits the column ...
const SIGDIR_KUCUK = 5;            // ... down to this
const SIGDIR_ADIM = 0.25;
const SATIR_ORANI = 1.2;
const AYRAC_ORANI = 1.1;           // space around the rule between two sections, in points per point of type
const UZUN_PUNTO = 10;             // the full wording on the sheets after the first
const UZUN_KENAR = { sol: 42, sag: 42, ust: 34, alt: 44 };
const DEVAM_BASLIK = 20;

/** Which blocks close each document (left to right) and the fields of each; every field is blank. */
const IMZA_BLOKLARI = {
  statik: ["sorumlu", "dekoratorKisa"],
  elektrik: ["dekoratorKisa", "elektrikSorumlusu"],
  isg: ["dekoratorTam"],
};
const BLOK_TANIMI = {
  sorumlu: { baslik: "sorumlu", alanlar: ["adSoyad", "unvan", "odaSicilNo", "cepTel", "imza"] },
  elektrikSorumlusu: { baslik: "elektrikSorumlusu", alanlar: ["adSoyad", "unvan", "odaSicilNo", "cepTel", "imza"] },
  dekoratorKisa: { baslik: "dekorator", alanlar: ["adSoyad", "tarih", "imzaKase"] },
  dekoratorTam: { baslik: "dekorator", alanlar: ["adSoyad", "unvan", "cepTel", "tarih", "imzaKase"] },
};
const KUTULU_ALANLAR = new Set(["imza", "imzaKase"]);

/** The signature blocks of a document as [{ baslik, alanlar }] keys (exported for the tests). */
export const imzaBloklari = (key) => (IMZA_BLOKLARI[key] || []).map((id) => BLOK_TANIMI[id]);

/** "Statik-Taahhutnamesi_11AF43_Sign-Istanbul-2026_TR.pdf" / "Structural-Undertaking_..._EN.pdf" */
export function projeTaahhutnameDosyaAdi(veri, dil) {
  const t = i18n.getFixedT(dil, "pdf");
  const h = veri.header || {};
  const parcalar = [
    t(`projeTaahhutname.dosyaOnEki.${veri.key}`),
    dosyaParcasi(h.standAdi),
    dosyaParcasi(h.fuarAdi),
    dil.toUpperCase(),
  ];
  return `${parcalar.filter(Boolean).join("_")}.pdf`;
}

/** "2026-09-23" → "23/09/2026" (dd/mm/yyyy everywhere, #560). */
const gunYaz = (tarih) => {
  const [y, a, g] = String(tarih || "").split("-");
  return y && a && g ? `${g.slice(0, 2)}/${a}/${y}` : "";
};

/** "23/09/2026 - 26/09/2026"; one date alone; "" when the fair has none. */
export function fuarTarihMetni(header) {
  const ilk = gunYaz(header?.fuarBaslangic);
  const son = gunYaz(header?.fuarBitis);
  if (ilk && son && ilk !== son) return `${ilk} - ${son}`;
  return ilk || son || "";
}

/** "5 - A10", "11a - 11AF43"; whichever is known when only one is. */
export const salonStandMetni = (header) => [header?.salonAdi, header?.standAdi].map((x) => String(x ?? "").trim()).filter(Boolean).join(" - ");

/**
 * Builds the PDF and returns its bytes.
 * @param {object} veri see the top of the file
 * @param {"tr"|"en"} dil
 * @param {{font: Uint8Array, fontKalin: Uint8Array, fontItalik: Uint8Array, sakaLogo: Uint8Array}} varliklar
 * @returns {Promise<Uint8Array>}
 */
export async function projeTaahhutnamePdfUret(veri, dil, varliklar) {
  if (!PROJE_TAAHHUTNAME_DILLERI.includes(dil)) throw new Error(`Unsupported language: ${dil}`);
  if (!PROJE_TAAHHUTNAME_ANAHTARLARI.includes(veri?.key)) throw new Error(`Unknown taahhutname: ${veri?.key}`);
  if (!varliklar?.font || !varliklar?.fontKalin || !varliklar?.fontItalik) throw new Error("The taahhutname PDF needs the regular, bold and italic font files");
  const [{ PDFDocument, rgb }, fontkitModulu] = await Promise.all([import("pdf-lib"), import("@pdf-lib/fontkit")]);
  const t = i18n.getFixedT(dil, "pdf");
  const m = (anahtar, degerler) => t(`projeTaahhutname.${anahtar}`, degerler);

  const h = veri.header || {};
  const baslik = String(veri.text?.baslik || "").trim() || m(`baslik.${veri.key}`);
  const firma = String(h.katilimciFirmaAdi || "").trim();
  const salonStand = salonStandMetni(h);

  const doc = await PDFDocument.create();
  doc.registerFontkit(fontkitModulu.default || fontkitModulu);
  doc.setTitle([baslik, firma].filter(Boolean).join(" - "));
  doc.setProducer("FDS");
  doc.setLanguage(dil);
  doc.setCreationDate(new Date());

  const font = await doc.embedFont(varliklar.font, { subset: true });
  const kalin = await doc.embedFont(varliklar.fontKalin, { subset: true });
  const italik = await doc.embedFont(varliklar.fontItalik, { subset: true });
  // bold italic has no file of its own: bold wins (the wording rarely asks for both)
  const fontOf = (k, i) => (k ? kalin : i ? italik : font);
  const destek = new Set(font.getCharacterSet());
  /** A character the font cannot draw would come out as an empty box; "?" says plainly that something was there. */
  const cizilebilir = (s) => { let c = ""; for (const k of String(s ?? "")) c += destek.has(k.codePointAt(0)) || k === " " ? k : "?"; return c; };

  const siyah = rgb(0, 0, 0);
  const gri = rgb(0.4, 0.4, 0.4);
  const acikGri = rgb(0.6, 0.6, 0.6);
  const y = (ust) => SAYFA.boy - ust;

  let sayfa = doc.addPage([SAYFA.en, SAYFA.boy]);

  const yaz = (f, metin, x, taban, punto, renk = siyah) => sayfa.drawText(cizilebilir(metin), { x, y: y(taban), size: punto, font: f, color: renk });
  const olc = (f, metin, punto) => f.widthOfTextAtSize(cizilebilir(metin), punto);
  /** The largest size ≤ punto (down to enAz) at which `metin` fits on one line of `genislik`. */
  const sigdir = (f, metin, punto, genislik, enAz = 5) => { let p = punto; while (p > enAz && olc(f, metin, p) > genislik) p -= 0.25; return p; };
  const cerceve = (x, ust, genislik, yukseklik, kalinlik = 0.75) => sayfa.drawRectangle({ x, y: y(ust + yukseklik), width: genislik, height: yukseklik, borderColor: siyah, borderWidth: kalinlik });
  const cizgi = (x1, ust1, x2, ust2, kalinlik = 0.5, renk = siyah) => sayfa.drawLine({ start: { x: x1, y: y(ust1) }, end: { x: x2, y: y(ust2) }, thickness: kalinlik, color: renk });

  /** Word wrap by measured width; "\n" breaks, a word wider than the cell is cut. */
  function sar(f, metin, punto, genislik) {
    const satirlar = [];
    for (const parca of String(metin ?? "").split("\n")) {
      let satir = "";
      for (const kelime of parca.split(/\s+/).filter(Boolean)) {
        let k = kelime;
        while (olc(f, k, punto) > genislik) {
          let n = k.length - 1;
          while (n > 1 && olc(f, k.slice(0, n), punto) > genislik) n -= 1;
          if (satir) { satirlar.push(satir); satir = ""; }
          satirlar.push(k.slice(0, n));
          k = k.slice(n);
        }
        const deneme = satir ? `${satir} ${k}` : k;
        if (satir && olc(f, deneme, punto) > genislik) { satirlar.push(satir); satir = k; } else satir = deneme;
      }
      satirlar.push(satir);
    }
    return satirlar;
  }

  const sigdirResim = (en, boy, kutu) => { const olcek = Math.min(kutu.en / en, kutu.boy / boy); return { w: en * olcek, h: boy * olcek }; };

  // ── the wording: blocks → lines of { metin, font, x } measured at a given type size ──
  function kelimele(parcalar) {
    const belirtecler = [];
    let bosluk = false;
    for (const p of parcalar) {
      if (p.satirSonu) { belirtecler.push({ satirSonu: true }); bosluk = false; continue; }
      const f = fontOf(p.kalin, p.italik);
      for (const [, bosPar, kelime] of p.metin.matchAll(/(\s+)|(\S+)/g)) {
        if (bosPar) { bosluk = true; continue; }
        belirtecler.push({ metin: kelime, font: f, bosluk });
        bosluk = false;
      }
    }
    return belirtecler;
  }
  function satirlaraBol(belirtecler, genislik, punto) {
    const bosGenislik = olc(font, " ", punto);
    const satirlar = [];
    let satir = [];
    let x = 0;
    const bitir = () => { satirlar.push(satir); satir = []; x = 0; };
    for (const b of belirtecler) {
      if (b.satirSonu) { bitir(); continue; }
      let metin = b.metin;
      let bosluk = satir.length && b.bosluk ? bosGenislik : 0;
      while (olc(b.font, metin, punto) > genislik) { // a word wider than the line is cut
        let n = metin.length - 1;
        while (n > 1 && olc(b.font, metin.slice(0, n), punto) > genislik) n -= 1;
        if (satir.length) bitir();
        satir.push({ metin: metin.slice(0, n), font: b.font, x: 0 });
        bitir();
        metin = metin.slice(n);
        bosluk = 0;
      }
      const w = olc(b.font, metin, punto);
      if (satir.length && x + bosluk + w > genislik) { bitir(); bosluk = 0; }
      satir.push({ metin, font: b.font, x: x + bosluk });
      x += bosluk + w;
    }
    if (satir.length || !satirlar.length) satirlar.push(satir);
    return satirlar;
  }
  /** Blocks → [{ satirlar, metinX, isaretX, isaret, sonra }] with the wrapping done at `punto`; `sonra` is the space after the block. */
  function duzenle(bloklar, { x, genislik, punto }) {
    const ic = punto * 0.7;
    const isaretGenislik = punto * 1.8;
    const adim = punto * 2.1;
    return bloklar.map((b) => {
      const isaretX = b.tur === "li" ? x + ic + b.derinlik * adim : null;
      const metinX = b.tur === "li" ? isaretX + isaretGenislik : x + (b.derinlik > 0 ? ic + isaretGenislik + (b.derinlik - 1) * adim : 0);
      const satirlar = satirlaraBol(kelimele(b.parcalar), x + genislik - metinX, punto);
      return { satirlar, metinX, isaretX, isaret: b.isaret, sonra: (b.tur === "li" ? 0.25 : 0.5) * punto };
    });
  }
  const duzenYuksekligi = (duzen, punto) => duzen.reduce((s, b, i) => s + b.satirlar.length * punto * SATIR_ORANI + (i < duzen.length - 1 ? b.sonra : 0), 0);
  /** The sections one under the other with a rule between them: the height, at `punto`. */
  const bolumlerYuksekligi = (duzenler, punto) => duzenler.reduce((s, d) => s + duzenYuksekligi(d, punto), 0) + Math.max(0, duzenler.length - 1) * AYRAC_ORANI * punto * 2;
  /** The largest type size (SIGDIR_BUYUK down to SIGDIR_KUCUK) at which the sections fit `yukseklik`; null when none does. */
  function sigdirBolumler(bolumler, { x, genislik, yukseklik }) {
    for (let p = SIGDIR_BUYUK; p >= SIGDIR_KUCUK - 1e-9; p -= SIGDIR_ADIM) {
      const duzenler = bolumler.map((b) => duzenle(b, { x, genislik, punto: p }));
      if (bolumlerYuksekligi(duzenler, p) <= yukseklik) return { punto: p, duzenler };
    }
    return null;
  }
  /** Draws laid-out sections from `ust` down, a rule between two of them; returns where it ended. */
  function bolumleriCiz(duzenler, punto, ust, sol, sag) {
    const lh = punto * SATIR_ORANI;
    let konum = ust;
    duzenler.forEach((duzen, n) => {
      if (n > 0) {
        konum += AYRAC_ORANI * punto;
        cizgi(sol, konum, sag, konum, 0.5, acikGri);
        konum += AYRAC_ORANI * punto;
      }
      duzen.forEach((b, i) => {
        b.satirlar.forEach((satir, k) => {
          const taban = konum + lh * 0.8;
          if (k === 0 && b.isaret) yaz(font, b.isaret, b.isaretX, taban, punto);
          for (const w of satir) yaz(w.font, w.metin, b.metinX + w.x, taban, punto);
          konum += lh;
        });
        if (i < duzen.length - 1) konum += b.sonra;
      });
    });
    return konum;
  }

  const bolumler = bolumlereAyir(htmlBloklari(veri.text?.icerik)).filter((b) => b.length);
  const sonBolumAyri = bolumler.length > 1;               // the last section sits in the signature band
  const kolonBolumleri = sonBolumAyri ? bolumler.slice(0, -1) : bolumler;
  const bantBolumleri = sonBolumAyri ? bolumler.slice(-1) : [];
  const bloklar = imzaBloklari(veri.key);

  // ── top row: fair logo | header | SAKA logo ──
  const solGenislik = XB - X0;
  const tabloEn = solGenislik - 2 * LOGO_HUCRE_EN;
  const tarih = fuarTarihMetni(h);
  const baslikSatirlari = [
    [m("baslikBilgisi.fuar"), String(h.fuarAdi || "")],
    ...(tarih ? [[m("baslikBilgisi.tarih"), tarih]] : []),
    [m("baslikBilgisi.katilimci"), String(h.katilimciFirmaAdi || "")],
    [m("baslikBilgisi.dekorator"), String(h.dekoratorFirmaAdi || "")],
    [m("baslikBilgisi.salonStand"), salonStand],
  ];
  const etiketEn = Math.min(84, Math.max(...baslikSatirlari.map(([e]) => olc(kalin, e, BASLIK_TABLO_PUNTO))) + 8);
  const degerEn = tabloEn - etiketEn - 8;
  const satirYuk = BASLIK_TABLO_PUNTO * SATIR_ORANI;
  const sarili = baslikSatirlari.map(([, d]) => sar(font, d, BASLIK_TABLO_PUNTO, degerEn));
  const tabloYuk = sarili.reduce((s, l) => s + l.length * satirYuk + 3, 0);
  const ilkSatirYuk = Math.max(ILK_SATIR_EN_AZ, tabloYuk + 6);

  cerceve(X0, Y0, LOGO_HUCRE_EN, ilkSatirYuk);
  cerceve(X0 + LOGO_HUCRE_EN, Y0, tabloEn, ilkSatirYuk);
  cerceve(XB - LOGO_HUCRE_EN, Y0, LOGO_HUCRE_EN, ilkSatirYuk);
  const kutuIci = { en: LOGO_HUCRE_EN - 2 * LOGO_IC, boy: ilkSatirYuk - 2 * LOGO_IC };
  const saka = await doc.embedPng(varliklar.sakaLogo);
  const sakaBoyut = sigdirResim(saka.width, saka.height, kutuIci);
  sayfa.drawImage(saka, {
    x: XB - LOGO_HUCRE_EN + (LOGO_HUCRE_EN - sakaBoyut.w) / 2, y: y(Y0 + (ilkSatirYuk + sakaBoyut.h) / 2), width: sakaBoyut.w, height: sakaBoyut.h,
  });
  const logoTuru = resimTuru(veri.logo);
  if (logoTuru) {
    const logo = logoTuru === "png" ? await doc.embedPng(veri.logo) : await doc.embedJpg(veri.logo);
    const b = sigdirResim(logo.width, logo.height, kutuIci);
    sayfa.drawImage(logo, { x: X0 + (LOGO_HUCRE_EN - b.w) / 2, y: y(Y0 + (ilkSatirYuk + b.h) / 2), width: b.w, height: b.h });
  }
  let tabloUst = Y0 + (ilkSatirYuk - tabloYuk) / 2;
  baslikSatirlari.forEach(([etiket], i) => {
    const x = X0 + LOGO_HUCRE_EN + 5;
    yaz(kalin, etiket, x, tabloUst + BASLIK_TABLO_PUNTO + 0.5, BASLIK_TABLO_PUNTO);
    sarili[i].forEach((s, k) => { if (s) yaz(font, s, x + etiketEn, tabloUst + BASLIK_TABLO_PUNTO + 0.5 + k * satirYuk, BASLIK_TABLO_PUNTO); });
    tabloUst += sarili[i].length * satirYuk + 3;
  });

  // ── title row ──
  const baslikUst = Y0 + ilkSatirYuk;
  cerceve(X0, baslikUst, solGenislik, BASLIK_YUK);
  const baslikPunto = sigdir(kalin, baslik, BASLIK_PUNTO, solGenislik - 16, 8);
  yaz(kalin, baslik, X0 + (solGenislik - olc(kalin, baslik, baslikPunto)) / 2, baslikUst + BASLIK_YUK / 2 + baslikPunto * 0.35, baslikPunto);

  // ── the signature band geometry (needed for the image box and the column to know where they end) ──
  const bandUst = Y1 - BAND_YUK;
  const resimUst = baslikUst + BASLIK_YUK;
  const resimYuk = bandUst - resimUst;

  // ── the source image the inspector chose ──
  cerceve(X0, resimUst, solGenislik, resimYuk);
  if (veri.kaynak?.bytes) {
    const { bytes, tur } = veri.kaynak;
    const kutu = { en: solGenislik - 8, boy: resimYuk - 8 };
    let gomulu;
    if (tur === "pdf") [gomulu] = await doc.embedPdf(bytes, [0]);
    else gomulu = tur === "png" ? await doc.embedPng(bytes) : await doc.embedJpg(bytes);
    const b = sigdirResim(gomulu.width, gomulu.height, kutu);
    const cizim = { x: X0 + (solGenislik - b.w) / 2, y: y(resimUst + (resimYuk + b.h) / 2), width: b.w, height: b.h };
    if (tur === "pdf") sayfa.drawPage(gomulu, cizim);
    else sayfa.drawImage(gomulu, cizim);
  }

  // ── the wording's column ──
  cerceve(XB, Y0, SAG_KOLON_EN, bandUst - Y0);
  const kolon = { x: XB + 5, genislik: SAG_KOLON_EN - 10, yukseklik: bandUst - Y0 - 10 };
  const kolonSigdi = kolonBolumleri.length ? sigdirBolumler(kolonBolumleri, kolon) : { punto: SIGDIR_BUYUK, duzenler: [] };

  // ── the signature band ──
  const bandGenislik = X1 - X0;
  const beyanEn = bantBolumleri.length ? bandGenislik * BAND_BEYAN_ORANI : 0;
  const bandBeyan = bantBolumleri.length ? { x: X0 + 5, genislik: beyanEn - 10, yukseklik: BAND_YUK - 10 } : null;
  const beyanSigdi = bandBeyan ? sigdirBolumler(bantBolumleri, bandBeyan) : { punto: SIGDIR_BUYUK, duzenler: [] };
  const uzun = (kolonBolumleri.length && !kolonSigdi) || (bandBeyan && !beyanSigdi);

  if (uzun) {
    // the full wording moves to the sheets after the first; this one says so
    const not = m("uzunMetinNotu");
    sar(kalin, not, 8, kolon.genislik).forEach((s, i) => yaz(kalin, s, kolon.x, Y0 + 14 + i * 10, 8));
  } else if (kolonSigdi.duzenler.length) {
    bolumleriCiz(kolonSigdi.duzenler, kolonSigdi.punto, Y0 + 6, XB + 5, X1 - 5);
  }

  if (bantBolumleri.length) {
    cerceve(X0, bandUst, beyanEn, BAND_YUK);
    if (uzun) {
      sar(kalin, m("uzunMetinNotu"), 8, bandBeyan.genislik).forEach((s, i) => yaz(kalin, s, bandBeyan.x, bandUst + 14 + i * 10, 8));
    } else {
      bolumleriCiz(beyanSigdi.duzenler, beyanSigdi.punto, bandUst + 6, bandBeyan.x, X0 + beyanEn - 5);
    }
  }

  const blokEn = Math.min(BAND_BLOK_EN_FAZLA, (bandGenislik - beyanEn) / bloklar.length);
  const bloklarSol = bantBolumleri.length ? X0 + beyanEn : X1 - blokEn * bloklar.length; // alone, the blocks sit at the right
  if (!bantBolumleri.length && bloklarSol - X0 > 1) cerceve(X0, bandUst, bloklarSol - X0, BAND_YUK); // the PHP sheet's empty box left of the signatures
  bloklar.forEach((blok, n) => {
    const x = bloklarSol + n * blokEn;
    cerceve(x, bandUst, blokEn, BAND_YUK);
    const baslikMetni = m(`imza.blok.${blok.baslik}`);
    yaz(kalin, baslikMetni, x + 5, bandUst + 11, sigdir(kalin, baslikMetni, 8, blokEn - 10, 6));
    cizgi(x, bandUst + BAND_BASLIK, x + blokEn, bandUst + BAND_BASLIK, 0.5, siyah);
    const alanlar = blok.alanlar.filter((a) => !KUTULU_ALANLAR.has(a));
    const kutuAlani = blok.alanlar.find((a) => KUTULU_ALANLAR.has(a));
    const alanSag = x + blokEn * 0.58;
    const etiketler = alanlar.map((a) => m(`imza.alan.${a}`));
    const etiketGenislik = Math.min(alanSag - x - 36, Math.max(...etiketler.map((e) => olc(font, e, 7))) + 6);
    etiketler.forEach((etiket, i) => {
      const taban = bandUst + BAND_BASLIK + BAND_ALAN_ARASI * (i + 1) - 4;
      yaz(font, etiket, x + 5, taban, sigdir(font, etiket, 7, etiketGenislik - 4, 5));
      cizgi(x + 5 + etiketGenislik, taban + 1.5, alanSag, taban + 1.5, 0.5, siyah);
    });
    if (kutuAlani) {
      const bx = alanSag + 7;
      const by = bandUst + BAND_BASLIK + 5;
      cerceve(bx, by, x + blokEn - 5 - bx, BAND_YUK - BAND_BASLIK - 10, 0.5);
      const etiket = m(`imza.alan.${kutuAlani}`);
      yaz(font, etiket, bx + 3, by + 8, sigdir(font, etiket, 6.5, x + blokEn - 5 - bx - 6, 5), gri);
    }
  });

  // ── a wording too long even for the smallest type: the full text on the sheets after the first ──
  if (uzun) {
    const sol = UZUN_KENAR.sol;
    const genislik = SAYFA.en - UZUN_KENAR.sol - UZUN_KENAR.sag;
    const altSinir = SAYFA.boy - UZUN_KENAR.alt;
    const lh = UZUN_PUNTO * SATIR_ORANI;
    let ust = 0;
    const yeniSayfa = () => {
      sayfa = doc.addPage([SAYFA.en, SAYFA.boy]);
      const kimlik = [baslik, firma, salonStand].filter(Boolean).join("  ·  ");
      yaz(font, kimlik, sol, UZUN_KENAR.ust + 7, 8, gri);
      cizgi(sol, UZUN_KENAR.ust + 12, SAYFA.en - UZUN_KENAR.sag, UZUN_KENAR.ust + 12, 0.5, acikGri);
      ust = UZUN_KENAR.ust + DEVAM_BASLIK;
    };
    const yer = (yukseklik) => { if (ust + yukseklik > altSinir) yeniSayfa(); };
    yeniSayfa();
    bolumler.forEach((bolum, n) => {
      if (n > 0) { yer(14); ust += 4; cizgi(sol, ust, SAYFA.en - UZUN_KENAR.sag, ust, 0.75, acikGri); ust += 9; }
      for (const b of duzenle(bolum, { x: sol, genislik, punto: UZUN_PUNTO })) {
        yer(Math.min(b.satirlar.length, 2) * lh); // never leave the first line of a block alone at the foot of a sheet
        for (let i = 0; i < b.satirlar.length; i += 1) {
          yer(lh);
          const taban = ust + lh * 0.8;
          if (i === 0 && b.isaret) yaz(font, b.isaret, b.isaretX, taban, UZUN_PUNTO);
          for (const w of b.satirlar[i]) yaz(w.font, w.metin, b.metinX + w.x, taban, UZUN_PUNTO);
          ust += lh;
        }
        ust += b.sonra;
      }
    });
  }

  // ── "n / N" only when there is more than the one sheet ──
  const sayfalar = doc.getPages();
  if (sayfalar.length > 1) {
    sayfalar.forEach((s, i) => {
      sayfa = s;
      const no = m("altBilgi.sayfa", { no: i + 1, toplam: sayfalar.length });
      yaz(font, no, SAYFA.en - UZUN_KENAR.sag - olc(font, no, 8), SAYFA.boy - 14, 8, gri);
    });
  }

  return doc.save();
}
